import { useToast } from 'vuestic-ui'

import { Entity } from '../../data/schema'
import { useUserStore } from '../../stores/user-store'
import { B64File } from '../b64file'
import { BaseQuery } from './baseQuery'
import { EntityResponse } from './entityResponse'

const { init: notify } = useToast()

/**
 * Single shared refresh promise.
 * While this exists, all requests await it instead of failing
 */
let refreshPromise: Promise<void> | null = null

/** List of available backend services */
export const backends = ['BaseBiz', 'Project', 'Ticket', 'Files'] as const
/** Type representing the available backend services */
export type Backends = (typeof backends)[number]

/** Base URLs for each backend, configured based on environment (development vs production) */
export const baseUrls: Record<Backends, string> = import.meta.env.DEV
  ? {
      BaseBiz: import.meta.env.VITE_LOCAL_BASEBIZ_URL as string,
      Project: import.meta.env.VITE_LOCAL_PROJECT_URL as string,
      Ticket: import.meta.env.VITE_LOCAL_TICKET_URL as string,
      Files: import.meta.env.VITE_LOCAL_FILES_URL as string,
    }
  : {
      BaseBiz: import.meta.env.VITE_PROD_BASEBIZ_URL as string,
      Project: import.meta.env.VITE_PROD_PROJECT_URL as string,
      Ticket: import.meta.env.VITE_PROD_TICKET_URL as string,
      Files: import.meta.env.VITE_PROD_FILES_URL as string,
    }

/**
 * Interface representing a server exception response.
 */
interface ServerException {
  /** Type of the exception */
  type: string
  /** Title of the exception */
  title: string
  /** HTTP status code */
  status: number
  /** Detailed description of the exception */
  detail: string
  /** Additional exception information */
  exception: {
    /** Exception details */
    details: string
    /** HTTP headers from the response */
    headers: Record<string, string[]>
    /** Request path that caused the exception */
    path: string
    /** API endpoint that caused the exception */
    endpoint: string
    /** Route values from the request */
    routeValues: Record<string, string>
  }
}

/**
 * Interface for requests containing a string list.
 */
export interface StringListRequest {
  /** Array of strings to send in the request */
  stringList: string[]
}

async function ensureFreshToken() {
  const store = useUserStore()
  if (!refreshPromise) {
    refreshPromise = store
      .refresh()
      .catch(async () => {
        await store.logout()
        throw new Error('Session expired')
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

function isTokenExpiringSoon(bufferSeconds = 90) {
  try {
    const store = useUserStore()
    const expiryDate = Number(store.jwtTokenExpiryDate)
    return Date.now() >= expiryDate - bufferSeconds * 1000
  } catch {
    return true
  }
}

/**
 * Sends an authenticated request to the specified backend.
 * Handles authentication, token refresh, and error notifications.
 * @param backend - The backend to send the request to
 * @param endpoint - The endpoint to send the request to
 * @param method - The HTTP method to use for the request
 * @param body - The request body (optional)
 * @param retry - Whether this is a retry attempt after token refresh (internal use)
 * @returns The response from the server
 */
export async function authedRequest(
  backend: Backends,
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: Entity | BaseQuery | B64File | StringListRequest,
  retry?: boolean,
): Promise<EntityResponse> {
  const store = useUserStore()
  if (isTokenExpiringSoon()) {
    // console.log("token is expiring soon, refreshing.")
    await ensureFreshToken()
  }
  const res = await fetch(`${baseUrls[backend]}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${store.jwtToken}`,
      TenantId: store.tenantId,
    },
    body: JSON.stringify(body),
  }).catch((e) => {
    const err = e as Error
    notify({
      message: err.message,
      color: 'danger',
    })
    throw err
  })
  if (res.status === 401) {
    if (retry) {
      const msg = 'Failed to authorize'
      notify({
        message: msg,
        color: 'danger',
      })
      await store.logout()
      throw new Error(msg)
    }
    await ensureFreshToken()
    return await authedRequest(backend, endpoint, method, body, true)
  }
  if (res.status == 403) {
    notify({
      message: 'Action Forbidden',
      color: 'danger',
      duration: 99999999999,
    })
    throw new Error('Action Forbidden')
  }
  const json = await res.json()
  if (res.status != 200) {
    const ex = json as ServerException
    notify({
      message: ex.title,
      color: 'danger',
      duration: 99999999999,
    })
    console.error(ex.title + '\n' + ex.detail, ex.exception)
    throw json
  }
  return json
}
